<template>
  <div
    class="flex flex-col gap-24 lg:flex-row lg:items-center lg:justify-between"
  >
    <div class="flex flex-col gap-16 md:gap-8">
      <h2 class="heading-2">
        {{ listingsCount }}
      </h2>
      <RevToggle
        id="toggle-listing-display"
        v-model="allListingsExpandedState"
        :label="i18n(translations.expandedView)"
      />
    </div>

    <div class="gap-16 space-y-12 lg:mt-0 lg:flex lg:gap-12 lg:space-y-0">
      <RevInputSelect
        id="number-of-listing-per-page"
        v-model="selectedListingsCount"
        class="lg:min-w-[14rem]"
        :label="i18n(translations.listingCountLabel)"
        :options="numberOfListingsPerPageOptions"
      />

      <RevInputSelect
        id="sortBy"
        v-model="sortByValue"
        class="lg:min-w-[14rem]"
        :label="i18n(translations.sortBy)"
        :options="sortByOptions"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevInputSelect } from '@ds/components/InputSelect'
import { RevToggle } from '@ds/components/Toggle'

import { PUBLICATION_STATE } from '~/scopes/listings/Listings.constants'

import type { InitialFilterValues } from '../Filters/Filters.types'

import {
  LISTINGS_PER_PAGE_COUNT_OPTIONS,
  SORT_OPTIONS,
} from './OrderingFilters.constants'
import translations from './OrderingFilters.translations'

const i18n = useI18n()

const props = defineProps<{
  activePublicationState: string
  count: number
  listingsPerPage: string
  sortBy: string
  submitFilters: () => void
  expandAllListings: (value: boolean) => void
}>()

const filterValues = defineModel<InitialFilterValues>('filterValues', {
  required: true,
})

const areAllListingsExpanded = defineModel<boolean>('areAllListingsExpanded', {
  required: true,
})

const listingsCount = computed(() => {
  let translationKey
  switch (props.activePublicationState) {
    case PUBLICATION_STATE.IN_VALIDATION:
      translationKey = translations.inValidationListingsCount
      break
    case PUBLICATION_STATE.DISABLED:
      translationKey = translations.archivedListingsCount
      break
    default:
      translationKey = translations.activeListingsCount
      break
  }

  return i18n(translationKey, { offersTotalCount: props.count })
})

const selectedListingsCount = computed({
  get: () => filterValues.value.pageSize,
  set: (value) => {
    filterValues.value.pageSize = value
    props.submitFilters()
  },
})

const allListingsExpandedState = computed({
  get: () => areAllListingsExpanded.value,
  set: (value) => {
    areAllListingsExpanded.value = value
    props.expandAllListings(value)
  },
})

const numberOfListingsPerPageOptions = LISTINGS_PER_PAGE_COUNT_OPTIONS.map(
  (value) => ({ label: value.toString(), value: value.toString() }),
)

const sortByOptions = SORT_OPTIONS.map((option) => ({
  ...option,
  label: i18n(option.label),
}))

const sortByValue = computed({
  get: () => filterValues.value.orderBy,
  set: (value) => {
    filterValues.value.orderBy = value
    props.submitFilters()
  },
})
</script>
